import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../components/Icon';
import { Button, Input, Select, Modal, Toast } from '../components/UI';
import { eventsAPI, youthAPI } from '../services/api';

// ===== Constants =====
const EVENT_TYPES = [
  { value: 'bayram', label: 'Bayram', icon: '🎉', color: '#f59e0b' },
  { value: 'sport', label: 'Sport', icon: '⚽', color: '#22c55e' },
  { value: 'madaniy', label: 'Madaniy', icon: '🎭', color: '#a855f7' },
  { value: 'talim', label: "Ta'limiy", icon: '📚', color: '#3b82f6' },
  { value: 'mehnat', label: 'Mehnat', icon: '🔨', color: '#ea580c' },
  { value: 'xayriya', label: 'Xayriya', icon: '💝', color: '#ec4899' },
  { value: 'harbiy', label: 'Harbiy-vatanparvarlik', icon: '🎖️', color: '#059669' },
  { value: 'ekologik', label: 'Ekologik', icon: '🌿', color: '#14b8a6' },
  { value: 'boshqa', label: 'Boshqa', icon: '📌', color: '#64748b' },
];

const EVENT_STATUSES = [
  { value: 'rejada', label: 'Rejada', color: '#6366f1', bg: '#eef2ff' },
  { value: 'faol', label: 'Faol', color: '#f59e0b', bg: '#fffbeb' },
  { value: 'tugadi', label: 'Tugadi', color: '#22c55e', bg: '#f0fdf4' },
  { value: 'bekor', label: 'Bekor', color: '#ef4444', bg: '#fef2f2' },
];

const PART_ROLES = [
  { value: 'ishtirokchi', label: 'Ishtirokchi' },
  { value: 'tashkilotchi', label: 'Tashkilotchi' },
  { value: "ko'ngilli", label: "Ko'ngilli" },
  { value: "ma'ruzachi", label: "Ma'ruzachi" },
];

const getET = (v) => EVENT_TYPES.find(t => t.value === v) || EVENT_TYPES[8];
const getES = (v) => EVENT_STATUSES.find(s => s.value === v) || EVENT_STATUSES[0];

const MONTHS_UZ = ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun', 'Iyul', 'Avgust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'];

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

// ===== Main Page =====
export default function PatrioticEventsPage() {
  const user = getUser();
  const isAdmin = user?.role === 'viloyat_admin' || user?.role === 'tuman_admin';
  const [tab, setTab] = useState('calendar'); // calendar | rating
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [rating, setRating] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Filters
  const [statusF, setStatusF] = useState('');
  const [typeF, setTypeF] = useState('');
  const [search, setSearch] = useState('');

  // Modals
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF) params.status = statusF;
      if (typeF) params.event_type = typeF;
      if (search) params.search = search;
      const [eRes, sRes, rRes] = await Promise.all([
        eventsAPI.list(params),
        eventsAPI.stats(),
        eventsAPI.rating(),
      ]);
      setEvents(eRes.data);
      setStats(sRes.data);
      setRating(rRes.data);
    } catch { showToast("Yuklanmadi", 'error'); }
    setLoading(false);
  }, [statusF, typeF, search]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    try { await eventsAPI.create(data); showToast("Tadbir yaratildi"); setFormOpen(false); load(); }
    catch { showToast("Xatolik", 'error'); }
  };
  const handleUpdate = async (data) => {
    try { await eventsAPI.update(editItem.id, data); showToast("Yangilandi"); setEditItem(null); load(); }
    catch { showToast("Xatolik", 'error'); }
  };
  const handleDelete = async () => {
    try { await eventsAPI.delete(deleteId); showToast("O'chirildi"); setDeleteId(null); load(); }
    catch { showToast("Xatolik", 'error'); }
  };

  const loadDetail = async (id) => {
    try { const r = await eventsAPI.get(id); setViewItem(r.data); } catch {}
  };
  const loadEdit = async (id) => {
    try { const r = await eventsAPI.get(id); setEditItem(r.data); } catch {}
  };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* ===== HERO ===== */}
      <div style={{
        background: 'linear-gradient(135deg, #065f46 0%, #047857 40%, #10b981 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -20, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: -30, right: 80, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 50, height: 50, borderRadius: 16, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>🇺🇿</div>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0 }}>Vatanparvarlik tadbirlari</h2>
              <p style={{ fontSize: 13, color: '#a7f3d0', margin: 0 }}>Tadbirlar taqvimi, ishtirokchilar va faol yoshlar reytingi</p>
            </div>
          </div>
          {!isAdmin && <Button onClick={() => setFormOpen(true)} icon="plus"
            style={{ background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.3)', color: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            Tadbir yaratish
          </Button>}
        </div>
        {/* Mini stats */}
        <div style={{ display: 'flex', gap: 20, marginTop: 20, flexWrap: 'wrap' }}>
          {[
            { l: 'Jami tadbirlar', v: stats.total || 0, i: 'calendar' },
            { l: 'Rejadagilar', v: stats.rejada || 0, i: 'clock' },
            { l: 'Faol', v: stats.faol || 0, i: 'zap' },
            { l: "O'tkazilgan", v: stats.tugadi || 0, i: 'checkCircle' },
            { l: 'Jami ishtiroklar', v: stats.total_participants || 0, i: 'users' },
            { l: 'Faol yoshlar', v: stats.unique_youth || 0, i: 'trophy' },
          ].map(s => (
            <div key={s.l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name={s.i} size={15} color="#a7f3d0" />
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s.v}</div>
                <div style={{ fontSize: 10, color: '#6ee7b7', fontWeight: 500 }}>{s.l}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== TAB SWITCHER ===== */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f1f5f9', borderRadius: 14, padding: 4, width: 'fit-content' }}>
        {[
          { id: 'calendar', label: 'Tadbirlar taqvimi', icon: 'calendar' },
          { id: 'rating', label: 'Faol yoshlar reytingi', icon: 'trophy' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
            background: tab === t.id ? '#fff' : 'transparent',
            color: tab === t.id ? 'var(--accent-dark)' : 'var(--text-muted)',
            boxShadow: tab === t.id ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
            border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .2s',
          }}>
            <Icon name={t.icon} size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* ===== CALENDAR TAB ===== */}
      {tab === 'calendar' && (
        <>
          {/* ADMIN: hudud jadvali */}
          {isAdmin && (stats.tuman_breakdown || stats.mfy_breakdown) ? (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>🏢</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                  {user?.role === 'viloyat_admin' ? 'Tumanlar' : 'MFYlar'} bo'yicha tadbirlar hisoboti
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
                  {(stats.tuman_breakdown || stats.mfy_breakdown || []).length} ta {user?.role === 'viloyat_admin' ? 'tuman' : 'MFY'}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 850 }}>
                  <thead>
                    <tr style={{ background: '#fafbff' }}>
                      {['#', user?.role === 'viloyat_admin' ? 'Tuman' : 'MFY', 'Jami', 'Rejada', 'Faol', 'Tugadi', 'Shu oy', 'Ishtirokchilar', 'Faol yoshlar'].map(h => (
                        <th key={h} style={{ padding: '12px 10px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '2px solid #e2e8f0', textAlign: h === '#' || h === 'Tuman' || h === 'MFY' ? 'left' : 'center', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(stats.tuman_breakdown || stats.mfy_breakdown || []).map((row, i) => {
                      const name = row[user?.role === 'viloyat_admin' ? 'tuman' : 'mfy'] || "Noma'lum";
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <td style={{ padding: '11px 10px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                          <td style={{ padding: '11px 10px', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{name}</td>
                          <td style={{ padding: '11px 10px', textAlign: 'center', fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{row.jami}</td>
                          <td style={{ textAlign: 'center' }}>{row.rejada_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#6366f1' }}>{row.rejada_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center' }}>{row.faol_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#ca8a04' }}>{row.faol_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center' }}>{row.tugadi_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#16a34a' }}>{row.tugadi_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center' }}>{row.shu_oy > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#fdf4ff', color: '#a855f7' }}>{row.shu_oy}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#0ea5e9' }}>{row.ishtirokchilar || 0}</td>
                          <td style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#059669' }}>{row.faol_yoshlar || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                      <td style={{ padding: '12px 10px' }} />
                      <td style={{ padding: '12px 10px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>JAMI</td>
                      {(() => {
                        const bd = stats.tuman_breakdown || stats.mfy_breakdown || [];
                        const sum = (k) => bd.reduce((s, r) => s + (r[k] || 0), 0);
                        return (
                          <>
                            <td style={{ textAlign: 'center', fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{sum('jami')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#6366f1' }}>{sum('rejada_c')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#ca8a04' }}>{sum('faol_c')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{sum('tugadi_c')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#a855f7' }}>{sum('shu_oy')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#0ea5e9' }}>{sum('ishtirokchilar')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#059669' }}>{sum('faol_yoshlar')}</td>
                          </>
                        );
                      })()}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            <>
              {/* MFY: filters + events */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto', paddingBottom: 4 }}>
                <Pill active={!statusF} onClick={() => setStatusF('')} label="Barchasi" count={stats.total || 0} />
                {EVENT_STATUSES.map(s => (
                  <Pill key={s.value} active={statusF === s.value} onClick={() => setStatusF(s.value)}
                    color={s.color} bg={s.bg} label={s.label}
                    count={s.value === 'rejada' ? stats.rejada : s.value === 'faol' ? stats.faol : s.value === 'tugadi' ? stats.tugadi : 0} />
                ))}
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 180, maxWidth: 320 }}>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Qidirish..."
                    style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 10, border: '1.5px solid var(--border)', background: '#fff', fontSize: 13, fontFamily: 'inherit', color: 'var(--text)' }} />
                  <div style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}><Icon name="search" size={15} /></div>
                </div>
                <select value={typeF} onChange={e => setTypeF(e.target.value)}
                  style={{ padding: '11px 14px', borderRadius: 10, border: '1.5px solid var(--border)', background: '#fff', fontSize: 13, fontFamily: 'inherit', color: 'var(--text)' }}>
                  <option value="">Barcha turlar</option>
                  {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                </select>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-dim)' }}>Yuklanmoqda...</div>
              ) : events.length === 0 ? (
                <EmptyEvents onAdd={() => setFormOpen(true)} />
              ) : (
                <EventTimeline events={events} onView={loadDetail} onEdit={loadEdit} onDelete={setDeleteId} />
              )}
            </>
          )}
        </>
      )}

      {/* ===== RATING TAB ===== */}
      {tab === 'rating' && <RatingTable data={rating} loading={loading} />}

      {/* ===== MODALS ===== */}
      <Modal open={formOpen || !!editItem} onClose={() => { setFormOpen(false); setEditItem(null); }}
        title={editItem ? 'Tadbirni tahrirlash' : 'Yangi tadbir'} width={640}>
        <EventForm event={editItem} onSave={editItem ? handleUpdate : handleCreate}
          onClose={() => { setFormOpen(false); setEditItem(null); }} />
      </Modal>

      <Modal open={!!viewItem} onClose={() => setViewItem(null)} title="Tadbir tafsilotlari" width={700}>
        {viewItem && <EventDetail event={viewItem} onRefresh={() => loadDetail(viewItem.id)} showToast={showToast} />}
      </Modal>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="O'chirishni tasdiqlang">
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>Ushbu tadbirni o'chirishni xohlaysizmi?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteId(null)}>Bekor qilish</Button>
          <Button variant="danger" onClick={handleDelete}>O'chirish</Button>
        </div>
      </Modal>
    </div>
  );
}

// ===== Pill =====
function Pill({ active, onClick, label, count, color, bg }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 20,
      border: active ? `2px solid ${color || 'var(--accent)'}` : '2px solid var(--border)',
      background: active ? (bg || 'var(--accent-light)') : '#fff',
      color: active ? (color || 'var(--accent)') : 'var(--text-muted)',
      fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
    }}>
      {label}
      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: active ? (color || 'var(--accent)') : '#e2e8f0', color: active ? '#fff' : 'var(--text-dim)' }}>{count}</span>
    </button>
  );
}

// ===== Empty =====
function EmptyEvents({ onAdd }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 20, border: '2px dashed var(--border)' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🇺🇿</div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Hali tadbir yaratilmagan</h3>
      <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 20 }}>Birinchi vatanparvarlik tadbiringizni yarating</p>
      <Button onClick={onAdd} icon="plus">Tadbir yaratish</Button>
    </div>
  );
}

// ===== EVENT TIMELINE =====
function EventTimeline({ events, onView, onEdit, onDelete }) {
  // Group events by month
  const groups = {};
  events.forEach(e => {
    const d = new Date(e.event_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(e);
  });

  const sorted = Object.keys(groups).sort();
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      {sorted.map(key => {
        const [y, m] = key.split('-');
        const monthName = MONTHS_UZ[parseInt(m) - 1];
        return (
          <div key={key} style={{ marginBottom: 28 }}>
            {/* Month header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: 'linear-gradient(135deg, #059669, #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14 }}>
                {m}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{monthName} {y}</div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{groups[key].length} ta tadbir</div>
              </div>
            </div>

            {/* Events */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 18, borderLeft: '2px solid #d1fae5' }}>
              {groups[key].map(e => {
                const et = getET(e.event_type);
                const es = getES(e.status);
                const isPast = e.event_date < today;
                const isToday = e.event_date === today;

                return (
                  <div key={e.id} onClick={() => onView(e.id)} style={{
                    background: '#fff', borderRadius: 14, padding: '16px 20px', cursor: 'pointer',
                    border: isToday ? '2px solid #10b981' : `1.5px solid var(--border)`,
                    boxShadow: isToday ? '0 0 0 3px #ecfdf5' : 'none',
                    opacity: isPast && e.status === 'tugadi' ? 0.75 : 1,
                    transition: 'all .2s', position: 'relative',
                  }}
                    onMouseEnter={x => { x.currentTarget.style.boxShadow = '0 6px 24px rgba(16,185,129,0.1)'; }}
                    onMouseLeave={x => { x.currentTarget.style.boxShadow = isToday ? '0 0 0 3px #ecfdf5' : 'none'; }}>

                    {/* Date circle on timeline */}
                    <div style={{
                      position: 'absolute', left: -30, top: 18, width: 22, height: 22, borderRadius: '50%',
                      background: isToday ? '#10b981' : es.value === 'tugadi' ? '#22c55e' : es.value === 'faol' ? '#f59e0b' : '#6366f1',
                      border: '3px solid #fff', boxShadow: '0 0 0 2px #d1fae5',
                    }} />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        {/* Badges */}
                        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          {isToday && <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0' }}>BUGUN</span>}
                          <span style={{ fontSize: 12, marginRight: 2 }}>{et.icon}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: et.color }}>{et.label}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: es.bg, color: es.color }}>{es.label}</span>
                        </div>

                        {/* Title + Date */}
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{e.title}</div>
                        <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-dim)', flexWrap: 'wrap' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Icon name="calendar" size={12} /> {e.event_date}
                            {e.event_time && ` • ${e.event_time.slice(0, 5)}`}
                          </span>
                          {e.location && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="building" size={12} /> {e.location}</span>}
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, color: 'var(--accent)' }}>
                            <Icon name="users" size={12} /> {e.participants_count} ishtirokchi
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }} onClick={x => x.stopPropagation()}>
                        <button onClick={() => onEdit(e.id)} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-dim)' }}>
                          <Icon name="edit" size={13} />
                        </button>
                        <button onClick={() => onDelete(e.id)} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--red)' }}>
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===== RATING TABLE =====
function RatingTable({ data, loading }) {
  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-dim)' }}>Yuklanmoqda...</div>;
  if (!data.length) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 20, border: '2px dashed var(--border)' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Hali reyting mavjud emas</h3>
      <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>Tadbirlarga ishtirokchilar qo'shilgach reyting paydo bo'ladi</p>
    </div>
  );

  const medals = ['🥇', '🥈', '🥉'];
  const maxScore = data[0]?.total_score || 1;

  return (
    <div>
      {/* Top 3 podium */}
      {data.length >= 3 && (
        <div className="podium-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 24 }}>
          {data.slice(0, 3).map((r, i) => (
            <div key={r.youth_id} style={{
              background: i === 0 ? 'linear-gradient(135deg, #fffbeb, #fef3c7)' : i === 1 ? 'linear-gradient(135deg, #f8fafc, #e2e8f0)' : 'linear-gradient(135deg, #fff7ed, #fed7aa)',
              borderRadius: 18, padding: '24px 20px', textAlign: 'center',
              border: i === 0 ? '2px solid #fbbf24' : i === 1 ? '2px solid #cbd5e1' : '2px solid #fdba74',
              order: i === 0 ? 1 : i === 1 ? 0 : 2,
            }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>{medals[i]}</div>
              <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff', background: i === 0 ? 'linear-gradient(135deg, #f59e0b, #d97706)' : i === 1 ? 'linear-gradient(135deg, #94a3b8, #64748b)' : 'linear-gradient(135deg, #f97316, #ea580c)' }}>
                {r.full_name.split(' ').map(w => w[0]).join('').slice(0, 2)}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{r.full_name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8 }}>{r.age} yosh • {r.gender === 'male' ? 'Erkak' : 'Ayol'}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: i === 0 ? '#d97706' : i === 1 ? '#475569' : '#ea580c' }}>{r.total_score}</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>ball • {r.events_count} tadbir</div>
            </div>
          ))}
        </div>
      )}

      {/* Full table */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="trophy" size={18} color="#f59e0b" />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Faol yoshlar reytingi</span>
          <span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 'auto' }}>{data.length} ta yosh</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#', 'Ism-familiya', 'Yosh', 'Jinsi', 'Tadbirlar', 'Ball', 'Progress'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={r.youth_id} style={{ borderBottom: '1px solid #f1f5f9' }}
                  onMouseEnter={x => { x.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={x => { x.currentTarget.style.background = ''; }}>
                  <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700, color: i < 3 ? '#f59e0b' : 'var(--text-dim)', width: 40 }}>
                    {i < 3 ? medals[i] : i + 1}
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg, var(--accent-light), #e0e7ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>
                        {r.full_name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.full_name}</div>
                        {r.phone && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{r.phone}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}>{r.age}</td>
                  <td style={{ padding: '12px 14px', fontSize: 12 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 6, background: r.gender === 'male' ? '#eff6ff' : '#fdf2f8', color: r.gender === 'male' ? '#3b82f6' : '#ec4899', fontWeight: 600 }}>
                      {r.gender === 'male' ? 'E' : 'A'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{r.events_count}</td>
                  <td style={{ padding: '12px 14px', fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{r.total_score}</td>
                  <td style={{ padding: '12px 14px', width: 140 }}>
                    <div style={{ height: 8, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 4, width: `${(r.total_score / maxScore) * 100}%`, background: i === 0 ? '#f59e0b' : i < 3 ? '#10b981' : 'var(--accent)', transition: 'width .5s' }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .podium-grid { grid-template-columns: 1fr 1fr 1fr; }
        @media (max-width: 768px) { .podium-grid { grid-template-columns: 1fr; }
          .podium-grid > div { order: unset !important; }
        }
      `}</style>
    </div>
  );
}

// ===== EVENT FORM =====
function EventForm({ event, onSave, onClose }) {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    event_type: event?.event_type || 'bayram',
    status: event?.status || 'rejada',
    event_date: event?.event_date || '',
    event_time: event?.event_time?.slice(0, 5) || '',
    end_time: event?.end_time?.slice(0, 5) || '',
    location: event?.location || '',
    organizer: event?.organizer || '',
    max_participants: event?.max_participants || 0,
    budget: event?.budget || '',
    result: event?.result || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      {/* Event type */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>Tadbir turi <span style={{ color: 'var(--red)' }}>*</span></label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {EVENT_TYPES.map(t => (
            <button key={t.value} onClick={() => set('event_type', t.value)} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 20,
              border: form.event_type === t.value ? `2px solid ${t.color}` : '2px solid var(--border)',
              background: form.event_type === t.value ? t.color + '15' : '#fff',
              color: form.event_type === t.value ? t.color : 'var(--text-muted)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      <Input label="Tadbir nomi" value={form.title} onChange={v => set('title', v)} required placeholder="Tadbir nomini kiriting..." />

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Tavsif</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Tadbir haqida..."
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
      </div>

      <div className="form-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Sana <span style={{ color: 'var(--red)' }}>*</span></label>
          <input type="date" value={form.event_date} onChange={e => set('event_date', e.target.value)}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit' }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Boshlanish</label>
          <input type="time" value={form.event_time} onChange={e => set('event_time', e.target.value)}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit' }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Tugash</label>
          <input type="time" value={form.end_time} onChange={e => set('end_time', e.target.value)}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="O'tkazilish joyi" value={form.location} onChange={v => set('location', v)} placeholder="Joy nomi..." />
        <Input label="Tashkilotchi" value={form.organizer} onChange={v => set('organizer', v)} placeholder="Tashkilotchi..." />
      </div>

      {event && (
        <Select label="Holat" value={form.status} onChange={v => set('status', v)}
          options={EVENT_STATUSES.map(s => ({ value: s.value, label: s.label }))} />
      )}

      {event?.status === 'tugadi' && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Natija</label>
          <textarea value={form.result} onChange={e => set('result', e.target.value)} rows={2} placeholder="Tadbir natijasi..."
            style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Button variant="ghost" onClick={onClose}>Bekor qilish</Button>
        <Button onClick={() => { if (form.title && form.event_date) onSave(form); }} disabled={!form.title || !form.event_date}>{event ? 'Saqlash' : 'Yaratish'}</Button>
      </div>

      <style>{`@media (max-width:640px) { .form-grid-3 { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

// ===== EVENT DETAIL =====
function EventDetail({ event: e, onRefresh, showToast }) {
  const [addOpen, setAddOpen] = useState(false);
  const [youthList, setYouthList] = useState([]);
  const [newPart, setNewPart] = useState({ youth: '', role: 'ishtirokchi', score: 5, note: '' });

  const et = getET(e.event_type);
  const es = getES(e.status);

  useEffect(() => {
    youthAPI.mini().then(r => setYouthList(r.data)).catch(() => {});
  }, []);

  const addParticipant = async () => {
    if (!newPart.youth) return;
    try {
      await eventsAPI.addParticipant(e.id, newPart);
      showToast("Ishtirokchi qo'shildi");
      setAddOpen(false);
      setNewPart({ youth: '', role: 'ishtirokchi', score: 5, note: '' });
      onRefresh();
    } catch (err) { showToast(err.response?.data?.error || 'Xatolik', 'error'); }
  };

  const removeP = async (yid) => {
    try { await eventsAPI.removeParticipant(e.id, yid); showToast("Olib tashlandi"); onRefresh(); }
    catch { showToast('Xatolik', 'error'); }
  };

  return (
    <div>
      {/* Badges */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13 }}>{et.icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: et.color }}>{et.label}</span>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius: 20, background: es.bg, color: es.color }}>{es.label}</span>
      </div>

      <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)', marginBottom: 12 }}>{e.title}</h3>

      {e.description && (
        <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 10 }}>{e.description}</p>
      )}

      {/* Info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        {[
          { i: 'calendar', l: 'Sana', v: `${e.event_date}${e.event_time ? ' • ' + e.event_time.slice(0, 5) : ''}${e.end_time ? ' — ' + e.end_time.slice(0, 5) : ''}` },
          { i: 'building', l: 'Joy', v: e.location },
          { i: 'users', l: 'Tashkilotchi', v: e.organizer },
          { i: 'users', l: 'Ishtirokchilar', v: `${e.participants_count}${e.max_participants ? ' / ' + e.max_participants : ''}` },
        ].map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: '#f8fafc' }}>
            <Icon name={r.i} size={15} color="var(--text-dim)" />
            <div><div style={{ fontSize: 10, color: 'var(--text-dim)' }}>{r.l}</div><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.v || '—'}</div></div>
          </div>
        ))}
      </div>

      {e.result && (
        <div style={{ padding: '12px 14px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 20, fontSize: 13, color: '#166534' }}>
          <strong>Natija:</strong> {e.result}
        </div>
      )}

      {/* Participants */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="users" size={16} /> Ishtirokchilar ({e.participants_list?.length || 0})
        </h4>
        <Button size="sm" variant="secondary" onClick={() => setAddOpen(!addOpen)} icon="plus">Qo'shish</Button>
      </div>

      {/* Add participant form */}
      {addOpen && (
        <div style={{ padding: '14px 16px', borderRadius: 12, background: '#f8fafc', border: '1px solid var(--border)', marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px', gap: 8, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Yosh</label>
              <select value={newPart.youth} onChange={x => setNewPart(p => ({ ...p, youth: x.target.value }))}
                style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 12, fontFamily: 'inherit' }}>
                <option value="">Tanlang...</option>
                {youthList.filter(y => !e.participants_list?.some(p => p.youth === y.id)).map(y => (
                  <option key={y.id} value={y.id}>{y.full_name} ({y.age}y)</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Rol</label>
              <select value={newPart.role} onChange={x => setNewPart(p => ({ ...p, role: x.target.value }))}
                style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 12, fontFamily: 'inherit' }}>
                {PART_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Ball</label>
              <input type="number" value={newPart.score} onChange={x => setNewPart(p => ({ ...p, score: parseInt(x.target.value) || 0 }))}
                style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 12, fontFamily: 'inherit' }} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
            <Button size="sm" variant="ghost" onClick={() => setAddOpen(false)}>Bekor</Button>
            <Button size="sm" onClick={addParticipant} disabled={!newPart.youth}>Qo'shish</Button>
          </div>
        </div>
      )}

      {/* Participants list */}
      {e.participants_list?.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#', 'Ism', 'Yosh', 'Rol', 'Ball', ''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {e.participants_list.map((p, i) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-dim)', fontWeight: 600 }}>{i + 1}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{p.youth_name}</div>
                    {p.youth_phone && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{p.youth_phone}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-muted)' }}>{p.youth_age}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#f1f5f9', color: 'var(--text-muted)' }}>
                      {PART_ROLES.find(r => r.value === p.role)?.label || p.role}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: p.score >= 15 ? '#f59e0b' : p.score >= 10 ? '#22c55e' : 'var(--text)' }}>{p.score}</span>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <button onClick={() => removeP(p.youth)} style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}>
                      <Icon name="x" size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-dim)', fontSize: 13 }}>Hali ishtirokchi qo'shilmagan</div>
      )}
    </div>
  );
}
